import { useState } from "react"

import { unpackNotebook, type createReportRequest } from "../utils/reportClient"
import type { Notebook, NotebookChatTurn } from "../utils/reportTypes"

export type ReportConversationTurn = Omit<NotebookChatTurn, "status"> & {
  status: NotebookChatTurn["status"] | "pending"
}

export function useReportConversation(
  notebook: Notebook | null,
  request: ReturnType<typeof createReportRequest>,
  onRecoverDocument: (notebook: Notebook) => void,
) {
  const [local, setLocal] = useState<Record<string, ReportConversationTurn[]>>(
    {},
  )
  const remember = (id: string, turns: ReportConversationTurn[]) => {
    setLocal((previous) => {
      const merged = new Map(
        (previous[id] ?? []).map((turn) => [turn.id, turn]),
      )
      turns.forEach((turn) => merged.set(turn.id, turn))
      const entries = [...merged.values()]
      const completed = new Set(
        entries
          .filter((turn) => turn.status !== "pending")
          .slice(-20)
          .map((turn) => turn.id),
      )
      return {
        ...previous,
        [id]: entries.filter(
          (turn) => turn.status === "pending" || completed.has(turn.id),
        ),
      }
    })
  }
  const turns = new Map(
    (local[notebook?.id ?? ""] ?? []).map((turn) => [turn.id, turn]),
  )
  notebook?.chat_history?.forEach((turn) => turns.set(turn.id, turn))

  const revise = async (
    instructions: string,
    signal: AbortSignal,
    persistDraft: (signal: AbortSignal) => Promise<Notebook>,
  ): Promise<Notebook> => {
    if (!notebook) {
      throw new Error("Open a notebook first.")
    }
    const pending: ReportConversationTurn = {
      id: crypto.randomUUID(),
      user_message: instructions,
      assistant_message: "Sofia is working on your request…",
      status: "pending",
      created_at: new Date().toISOString(),
      revision_before: notebook.revision,
    }
    remember(notebook.id, [pending])
    let submitted = false
    try {
      const current = await persistDraft(signal)
      pending.revision_before = current.revision
      submitted = true
      const loaded = unpackNotebook(
        await request<Notebook | { notebook: Notebook }>(
          `/api/notebooks/${encodeURIComponent(current.id)}/revise`,
          {
            instructions,
            expected_revision: current.revision,
            request_id: pending.id,
          },
          "POST",
          signal,
        ),
      )
      if (signal.aborted) {
        return loaded
      }
      const recorded = loaded.chat_history?.find(
        (turn) => turn.id === pending.id,
      )
      const unchanged = loaded.revision === current.revision
      remember(notebook.id, [
        recorded ?? {
          ...pending,
          status: unchanged ? "unchanged" : "applied",
          revision_after: loaded.revision,
          assistant_message: unchanged
            ? "No notebook changes were made."
            : `Saved revision ${loaded.revision}. The changed sections are highlighted in your notebook.`,
        },
      ])
      return loaded
    } catch (failure: unknown) {
      if (!signal.aborted) {
        remember(notebook.id, [
          {
            ...pending,
            status: "failed",
            assistant_message:
              failure instanceof Error
                ? failure.message
                : "The request could not be completed. Please try again.",
          },
        ])
        if (submitted) {
          // A failed HTTP response can still have a durable server-side outcome.
          // Drafts were saved before submission and controls stay disabled.
          // Recover the committed document too if only its response was lost.
          try {
            const recovered = unpackNotebook(
              await request<Notebook | { notebook: Notebook }>(
                `/api/notebooks/${encodeURIComponent(notebook.id)}`,
                undefined,
                "GET",
                signal,
              ),
            )
            if (!signal.aborted) {
              remember(notebook.id, recovered.chat_history ?? [])
              const outcome = recovered.chat_history?.find(
                (turn) => turn.id === pending.id,
              )
              if (outcome && outcome.status !== "failed") {
                return recovered
              }
              // A conflict may leave the server ahead of the document we submitted.
              // Keep the failure and prompt, but let a retry use the current revision.
              onRecoverDocument(recovered)
            }
          } catch {
            // Keep the exact request and failure visible if reconnecting fails.
          }
        }
      }
      throw failure
    }
  }
  return {
    turns: [...turns.values()].sort((left, right) =>
      left.created_at.localeCompare(right.created_at),
    ),
    revise,
  }
}
